import type { Namespace, Visibility } from './config';
import Logger from '@eneco/common/log';
import { safeStorage } from './safe-storage';

/**
 * Pending OAuth storage key prefix
 * Used to persist sasUrl across page redirects for seamless SSO
 */
const PENDING_OAUTH_KEY_PREFIX = 'chat:pending-oauth:';

/**
 * Time-to-live for pending OAuth data (10 minutes)
 * Prevents stale sasUrls from being used after extended periods
 */
const PENDING_OAUTH_TTL = 10 * 60 * 1000;

/**
 * Data structure for pending OAuth authentication
 * Stored when user initiates login and cleared after token submission
 */
export interface PendingOAuthData {
  /** The sasUrl to POST tokens to after authentication */
  sasUrl: string;
  /** Timestamp when the pending OAuth was created */
  timestamp: number;
  /** Visibility state to restore after redirect (e.g., 'compact', 'full-page') */
  visibility?: Visibility;
}

/**
 * Get the storage key for pending OAuth data
 * @param namespace - The namespace to scope the storage key
 */
function getPendingOAuthKey(namespace: Namespace): string {
  return `${PENDING_OAUTH_KEY_PREFIX}${namespace}`;
}

/**
 * Save pending OAuth data to localStorage
 * Called when user clicks login and needs to redirect to Okta
 *
 * @param namespace - The namespace for storage key scoping
 * @param sasUrl - The sasUrl to POST tokens to after authentication
 * @param visibility - Optional visibility state to restore after redirect
 */
export function savePendingOAuth(namespace: Namespace, sasUrl: string, visibility?: Visibility): void {
  try {
    const data: PendingOAuthData = {
      sasUrl,
      timestamp: Date.now(),
      visibility,
    };
    safeStorage.setItem(getPendingOAuthKey(namespace), JSON.stringify(data));
    Logger.info('o1a2b3c1', 'Pending OAuth saved for redirect flow');
  } catch (error) {
    Logger.error('o1a2b3c2', 'Failed to save pending OAuth:', error);
  }
}

/**
 * Get pending OAuth data from localStorage
 * Returns null if no pending OAuth exists or if it has expired
 *
 * @param namespace - The namespace for storage key scoping
 * @returns PendingOAuthData if valid and not expired, null otherwise
 */
export function getPendingOAuth(namespace: Namespace): PendingOAuthData | null {
  try {
    const stored = safeStorage.getItem(getPendingOAuthKey(namespace));
    if (!stored) {
      return null;
    }

    const data: PendingOAuthData = JSON.parse(stored);

    // Check if the pending OAuth has expired (TTL check)
    if (Date.now() - data.timestamp > PENDING_OAUTH_TTL) {
      Logger.info('o1a2b3c3', 'Pending OAuth expired, clearing');
      clearPendingOAuth(namespace);
      return null;
    }

    return data;
  } catch (error) {
    Logger.error('o1a2b3c4', 'Failed to read pending OAuth:', error);
    return null;
  }
}

/**
 * Clear pending OAuth data from localStorage
 * Called after successful token submission or when the data expires
 *
 * @param namespace - The namespace for storage key scoping
 */
export function clearPendingOAuth(namespace: Namespace): void {
  try {
    safeStorage.removeItem(getPendingOAuthKey(namespace));
    Logger.info('o1a2b3c5', 'Pending OAuth cleared');
  } catch (error) {
    Logger.error('o1a2b3c6', 'Failed to clear pending OAuth:', error);
  }
}
